/**
 * Checks if a URL is a Wistia share (/s/) URL.
 * @param {string} url - The URL to check.
 * @return {boolean} True if the URL is a Wistia share URL.
 */
export const isWistiaShareUrl = url =>
    Boolean(url && url.includes('wistia') && url.includes('/s/'));

/**
 * Resolves a Wistia URL to its embed media ID.
 * For /medias/ URLs, extracts the ID directly.
 * For /s/ (share) URLs, uses the Wistia oEmbed API to resolve the share token
 * to a real media hashed ID.
 * @param {string} url - The Wistia URL to resolve.
 * @return {Promise<string>} The resolved media ID.
 */
export const resolveWistiaMediaId = async url => {
    if (!url?.includes('wistia')) {
        return url ? url.split('/').pop() : '';
    }

    if (url.includes('/medias/')) {
        return url.split('/').pop();
    }

    if (url.includes('/s/')) {
        try {
            const response = await fetch(
                `https://fast.wistia.com/oembed?url=${encodeURIComponent(url)}&format=json`
            );

            if (!response.ok) {
                return url.split('/').pop();
            }

            const data = await response.json();

            const match = data.html?.match(/wistia_async_([a-z0-9]+)/i);
            if (match) {
                return match[1];
            }

            const iframeMatch = data.html?.match(/embed\/iframe\/([a-z0-9]+)/i);
            if (iframeMatch) {
                return iframeMatch[1];
            }

            return url.split('/').pop();
        } catch {
            return url.split('/').pop();
        }
    }

    return url.split('/').pop();
};

/**
 * Resolves a Wistia /s/ share URL to a /medias/ URL with the real media ID.
 * Non-share URLs are returned unchanged.
 * @param {string} url - The video URL.
 * @return {Promise<string>} The resolved URL.
 */
export const resolveWistiaShareUrl = async url => {
    if (!isWistiaShareUrl(url)) {
        return url;
    }
    const shareToken = url.split('/').pop();
    const mediaId = await resolveWistiaMediaId(url);
    if (mediaId === shareToken) {
        return url;
    }
    const origin = url.replace(/\/s\/[a-z0-9]+$/i, '');
    return `${origin}/medias/${mediaId}`;
};

/**
 * Resolves Wistia /s/ share URLs in both a body HTML string and a videoUrl.
 * Returns an object with only the fields that changed.
 * @param {Object} params - The params object.
 * @param {string} params.body - The HTML body string.
 * @param {string} params.videoUrl - The video URL.
 * @return {Promise<Object>} An object with updated fields (body, videoUrl), empty if unchanged.
 */
export const resolveWistiaContent = async ({ body, videoUrl } = {}) => {
    const updates = {};

    if (typeof body === 'string' && body.includes('/s/') && body.includes('wistia')) {
        const updatedBody = await resolveWistiaShareUrlsInBody(body);
        if (updatedBody !== body) {
            updates.body = updatedBody;
        }
    }

    if (isWistiaShareUrl(videoUrl)) {
        updates.videoUrl = await resolveWistiaShareUrl(videoUrl);
    }

    return updates;
};

/**
 * Scans an HTML body string for Wistia /s/ share URLs and resolves each
 * to a /medias/{mediaId} URL using the oEmbed API. Returns the updated body.
 * @param {string} body - The HTML body string to scan.
 * @return {Promise<string>} The body with resolved URLs.
 */
export const resolveWistiaShareUrlsInBody = async body => {
    if (!body || !body.includes('/s/') || !body.includes('wistia')) {
        return body;
    }

    const shareUrlPattern = /https:\/\/[a-z]+\.wistia\.com\/s\/[a-z0-9]+/gi;
    const shareUrls = body.match(shareUrlPattern);

    if (!shareUrls || shareUrls.length === 0) {
        return body;
    }

    const uniqueUrls = [...new Set(shareUrls)];
    let updatedBody = body;

    const resolutions = await Promise.all(uniqueUrls.map(resolveWistiaMediaId));

    uniqueUrls.forEach((shareUrl, index) => {
        const mediaId = resolutions[index];
        const shareToken = shareUrl.split('/').pop();
        if (mediaId === shareToken) {
            return;
        }
        const origin = shareUrl.replace(/\/s\/[a-z0-9]+$/i, '');
        updatedBody = updatedBody.split(shareUrl).join(`${origin}/medias/${mediaId}`);
    });

    return updatedBody;
};
